import { useEffect, useCallback, useState } from 'react';
import { HeartIcon, HeartFillIcon, IconButton } from '@eventbrite/marmalade';
import { useBasicInfo } from '@/src/listings/components/_shared/contexts/BasicInfoContext';
import { defineMessages, useIntl } from 'react-intl';
import { withTooltip } from '@eventbrite/legacy-eds.tooltip';
import { useUserData } from '@/src/listings/hooks/useUserData';
import classNames from 'classnames';
import { useBookmarkActions } from './hooks';
import styles from './LikeEventButton.module.scss';

const IconButtonWithTooltip = withTooltip(IconButton);

const messages = defineMessages({
  like: {
    id: 'Like event',
    defaultMessage: 'Like event',
    description: 'Like event button tooltip text',
  },
  unlike: {
    id: 'Unlike event',
    defaultMessage: 'Unlike event',
    description: 'Unlike event button tooltip text',
  },
});

export const LikeEventButton = () => {
  const { id: eventId } = useBasicInfo();
  const { formatMessage } = useIntl();
  const { hasLikedEvent, isLoading: userDataLoading } = useUserData();
  const [isLiked, setIsLiked] = useState(false);

  useEffect(() => {
    setIsLiked(hasLikedEvent);
  }, [hasLikedEvent]);

  const { likeEvent, unlikeEvent, bookmarkActionLoading } = useBookmarkActions(eventId, setIsLiked);
  const isLoading = userDataLoading || bookmarkActionLoading;

  // Handle post-login bookmark action
  useEffect(() => {
    const urlParams = new URLSearchParams(window?.location?.search);
    const bookmarkEventId = urlParams.get('bookmarkEvent');

    if (bookmarkEventId === eventId && !isLiked && !isLoading) {
      // Remove the param from URL to prevent re-triggering
      const newUrl = new URL(window.location.href);
      newUrl.searchParams.delete('bookmarkEvent');
      window.history.replaceState({}, '', newUrl.toString());

      // Trigger the like action
      likeEvent();
    }
  }, [eventId, isLiked, isLoading, likeEvent]);

  const handleClick = useCallback(() => {
    if (isLoading) return;

    if (isLiked) {
      unlikeEvent();
    } else {
      likeEvent();
    }
  }, [isLiked, isLoading, likeEvent, unlikeEvent]);

  const tooltipText = formatMessage(isLiked ? messages.unlike : messages.like);

  const buttonClassName = classNames(styles.likeButton, {
    [styles.liked]: isLiked,
  });

  const heartIcon = isLiked ? <HeartFillIcon /> : <HeartIcon />;

  return (
    <div className={styles.likeButtonContainer}>
      <IconButtonWithTooltip
        icon={heartIcon}
        onClick={handleClick}
        aria-label={tooltipText}
        aria-pressed={isLiked}
        tooltipId="tooltip-bookmark"
        tooltipText={tooltipText}
        tooltipArrowLocation="bottom"
        enhancedA11y={true}
        className={buttonClassName}
        disabled={isLoading}
      />
    </div>
  );
};
